import type { ReactNode } from 'react'
import {
  Button,
  Center,
  Loader,
  Modal as MantineModal,
  Pagination as MantinePagination,
  Paper,
} from '@mantine/core'
import { useRouter } from '@tanstack/react-router'
import type { ErrorComponentProps } from '@tanstack/react-router'

/* Shared primitives for the Lugstash operator console. Generic controls are
   Mantine components (themed in __root.tsx); design signatures — LED status
   badges, mono table headers, kickers — stay custom. */

export const kickerCls =
  'font-mono text-[0.62rem] font-semibold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500'

/* `whitespace-nowrap`: these are short uppercase labels with wide letter
   tracking, so a wrapped one reads as a column of single letters rather than a
   heading. Narrow viewports are the ScrollContainer's job, not the header's. */
export const thCls =
  'whitespace-nowrap px-4 py-2.5 text-left font-mono text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500'

export const tdCls = 'px-4 py-3 text-sm text-slate-700 dark:text-slate-300'

const STATUS_DOT: Record<string, string> = {
  active: 'bg-emerald-500',
  provisioning: 'bg-blue-500',
  suspended: 'bg-amber-500',
  revoked: 'bg-red-500',
  offline: 'bg-slate-400 dark:bg-slate-600',
  completed: 'bg-emerald-500',
  paid: 'bg-emerald-500',
  pending: 'bg-amber-500',
  failed: 'bg-red-500',
  refunded: 'bg-blue-500',
  ok: 'bg-emerald-500',
  duplicate: 'bg-blue-500',
}

export function StatusBadge({ status }: { status: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-xs text-slate-600 dark:text-slate-300">
      <span
        className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[status] ?? 'bg-slate-400 dark:bg-slate-600'}`}
      />
      {status}
    </span>
  )
}

export function Panel({
  className = '',
  children,
}: {
  className?: string
  children: ReactNode
}) {
  return (
    <Paper withBorder radius={12} className={className}>
      {children}
    </Paper>
  )
}

export function PageHeader({
  kicker,
  title,
  actions,
}: {
  kicker: string
  title: string
  actions?: ReactNode
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <p className={`${kickerCls} mb-1`}>{kicker}</p>
        <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 font-stretch-[112%] dark:text-white">
          {title}
        </h1>
      </div>
      {actions ? <div className="flex gap-2">{actions}</div> : null}
    </div>
  )
}

export function Modal({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
}) {
  return (
    <MantineModal
      opened={open}
      onClose={onClose}
      title={title}
      radius={12}
      classNames={{
        title: 'text-sm font-bold text-slate-900 dark:text-white',
      }}
    >
      <div className="space-y-4">{children}</div>
    </MantineModal>
  )
}

export function Pagination({
  page,
  pages,
  onChange,
}: {
  page: number
  pages: number
  onChange: (page: number) => void
}) {
  if (pages <= 1) return null
  return <MantinePagination total={pages} value={page} onChange={onChange} size="sm" radius={8} />
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <Center className="gap-2 py-12 font-mono text-xs uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
      <Loader size="xs" />
      {label}
    </Center>
  )
}

export function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <Panel className="px-6 py-16 text-center">
      <svg
        viewBox="0 0 16 16"
        width="24"
        height="24"
        aria-hidden="true"
        className="mx-auto mb-4 text-slate-300 dark:text-slate-700"
      >
        <rect x="0" y="0" width="7" height="7" rx="1.5" fill="currentColor" />
        <rect x="9" y="0" width="7" height="7" rx="1.5" fill="currentColor" />
        <rect x="0" y="9" width="7" height="7" rx="1.5" fill="currentColor" />
        <rect x="9" y="9" width="7" height="7" rx="1.5" fill="currentColor" />
      </svg>
      <h2 className="m-0 mb-1 text-base font-semibold text-slate-900 dark:text-white">{title}</h2>
      <p className="m-0 text-sm text-slate-500 dark:text-slate-400">{body}</p>
    </Panel>
  )
}

export function AdminRouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter()
  const message = error instanceof Error ? error.message : String(error)
  const missingRole = /admin role/i.test(message)

  const retry = () => {
    reset()
    void router.invalidate()
  }

  return (
    <Paper
      withBorder
      radius={12}
      className="border-red-300 p-6 dark:border-red-500/40"
    >
      <p className={`${kickerCls} mb-2 text-red-600 dark:text-red-400`}>Request failed</p>
      <h2 className="m-0 mb-2 text-lg font-bold tracking-tight text-slate-900 font-stretch-[112%] dark:text-white">
        {missingRole ? 'Your account is missing the admin role' : 'This page could not load'}
      </h2>
      <p className="m-0 mb-4 max-w-xl text-sm text-slate-600 dark:text-slate-400">
        {missingRole
          ? 'You are signed in, but the API requires the admin role. Ask an administrator to grant it to your account in Zitadel, then sign out and back in.'
          : 'The API request behind this page failed. Retry, or check that the backend is reachable.'}
      </p>
      <p className="m-0 mb-5 break-all font-mono text-xs text-slate-400 dark:text-slate-500">
        {message}
      </p>
      <Button variant="default" onClick={retry}>
        Try again
      </Button>
    </Paper>
  )
}

export const formatDate = (v?: string | null) => (v ? new Date(v).toLocaleString() : '—')

export const totalPages = (total: number, limit: number) =>
  Math.max(1, Math.ceil(total / Math.max(1, limit)))
